import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config/config';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { getAdminMenuItems } from '../../utils/menuItems';
import '../../styles/ManajemenUserPage.css';

const ManajemenUserPage = ({ initialTab = 'users' }) => {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [users, setUsers] = useState([]);
    const [users2FA, setUsers2FA] = useState([]);
    const [pendingUsers, setPendingUsers] = useState([]);
    const [klOptions, setKlOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterKL, setFilterKL] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetType, setResetType] = useState('');
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
    const [itemsPerPage, setItemsPerPage] = useState(10); // Default for table
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [selectedPendingUser, setSelectedPendingUser] = useState(null);

    // Form state untuk tambah user
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        fullname: '',
        email: '',
        hp: '',
        role: '',
        kl: '',
        direktorat: ''
    });

    const navigate = useNavigate();

    const roleOptions = [
        { value: 0, label: 'Verifikator' },
        { value: 1, label: 'Kementerian / Lembaga' },
        { value: 2, label: 'Supervisor' },
        { value: 3, label: 'Publikator' }
    ];

    useEffect(() => {
        const checkAccess = () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/');
                return;
            }

            try {
                const decodedToken = jwtDecode(token);
                const allowedRoles = [0, 2];
                if (!allowedRoles.includes(decodedToken.role)) {
                    navigate('/');
                    return;
                }
            } catch (error) {
                console.error('Invalid token:', error);
                navigate('/');
            }
        };

        checkAccess();
        fetchUsers();
        fetchUsers2FA();
        fetchKlOptions();
        fetchPendingUsers();
    }, [navigate]);

    useEffect(() => {
        if (viewMode === 'table') {
            setItemsPerPage(10);
        } else {
            setItemsPerPage(6);
        }
        setCurrentPage(1); // Reset page when view changes
    }, [viewMode]);


    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.API_URL}/muser/users`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setUsers(response.data.users);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Gagal mengambil daftar user');
        }
    };

    const fetchUsers2FA = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.API_URL}/muser/users-2fa`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setUsers2FA(response.data.users);
            }
        } catch (error) {
            console.error('Error fetching 2FA users:', error);
        }
    };

    const fetchKlOptions = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.API_URL}/muser/kl-options`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setKlOptions(response.data.kl);
            }
        } catch (error) {
            console.error('Error fetching KL options:', error);
        }
    };

    const fetchPendingUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.API_URL}/api/register/pending`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setPendingUsers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching pending users:', error);
        }
    };

    const handleApproveUser = async (userId) => {
        if (!window.confirm('Apakah Anda yakin ingin menyetujui pendaftaran user ini?')) {
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.API_URL}/api/register/approve/${userId}`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                toast.success('Pendaftaran berhasil disetujui!');
                fetchPendingUsers();
                fetchUsers();
            }
        } catch (error) {
            console.error('Error approving user:', error);
            toast.error(error.response?.data?.message || 'Gagal menyetujui pendaftaran');
        } finally {
            setLoading(false);
        }
    };

    const handleRejectUser = async () => {
        if (!rejectReason.trim()) {
            toast.error('Alasan penolakan wajib diisi');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.API_URL}/api/register/reject/${selectedPendingUser.id}`,
                { reason: rejectReason },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                toast.success('Pendaftaran berhasil ditolak');
                setShowRejectModal(false);
                setRejectReason('');
                setSelectedPendingUser(null);
                fetchPendingUsers();
            }
        } catch (error) {
            console.error('Error rejecting user:', error);
            toast.error(error.response?.data?.message || 'Gagal menolak pendaftaran');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();

        // Validasi - KL menjadi mandatory, fullname & email tidak
        const requiredFields = ['username', 'password', 'role', 'kl'];
        const fieldLabels = {
            username: 'Username',
            password: 'Password',
            role: 'Role',
            kl: 'Kementerian/Lembaga'
        };

        for (let field of requiredFields) {
            if (!newUser[field]) {
                toast.error(`${fieldLabels[field]} tidak boleh kosong`);
                return;
            }
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.API_URL}/muser/add-user`,
                newUser,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                setNewUser({
                    username: '',
                    password: '',
                    fullname: '',
                    email: '',
                    hp: '',
                    role: '',
                    kl: '',
                    direktorat: ''
                });
                setShowAddModal(false);
                fetchUsers();
            }
        } catch (error) {
            console.error('Error adding user:', error);
            const errorMessage = error.response?.data?.message || 'Terjadi kesalahan saat menambah user';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser) return;

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.API_URL}/muser/reset-password`,
                { username: selectedUser.username },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                setShowResetModal(false);
                setSelectedUser(null);
            }
        } catch (error) {
            console.error('Error resetting password:', error);
            const errorMessage = error.response?.data?.message || 'Terjadi kesalahan saat reset password';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleReset2FA = async () => {
        if (!selectedUser) return;

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.API_URL}/muser/reset-2fa`,
                { username: selectedUser.username },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                setShowResetModal(false);
                setSelectedUser(null);
                fetchUsers2FA();
            }
        } catch (error) {
            console.error('Error resetting 2FA:', error);
            const errorMessage = error.response?.data?.message || 'Terjadi kesalahan saat reset 2FA';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const openResetModal = (user, type) => {
        setSelectedUser(user);
        setResetType(type);
        setShowResetModal(true);
    };

    const getRoleName = (role) => {
        const roleOption = roleOptions.find(r => r.value === role);
        return roleOption ? roleOption.label : 'Unknown';
    };

    const getKlName = (kodeKl) => {
        const kl = klOptions.find(k => k.kode_kl === kodeKl);
        return kl ? kl.nama_kl : kodeKl;
    };

    // Filter users berdasarkan search term, role, dan KL
    const filteredUsers = users.filter(user => {
        const username = user.username || '';
        const fullname = user.fullname || '';
        const email = user.email || '';
        const searchLower = searchTerm.toLowerCase();

        const matchesSearch = username.toLowerCase().includes(searchLower) ||
            fullname.toLowerCase().includes(searchLower) ||
            email.toLowerCase().includes(searchLower);
        
        const matchesRole = !filterRole || user.role === parseInt(filterRole);
        const matchesKL = !filterKL || user.kl === filterKL;

        return matchesSearch && matchesRole && matchesKL;
    });

    const filteredUsers2FA = users2FA.filter(user => {
        const username = user.username || '';
        return username.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Pagination logic
    const currentData = activeTab === 'users' ? filteredUsers : filteredUsers2FA;
    const totalPages = Math.ceil(currentData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = currentData.slice(startIndex, endIndex);

    // Reset ke halaman 1 ketika search berubah atau tab berubah
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activeTab]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleNewUserChange = (e) => {
        const { name, value } = e.target;
        setNewUser(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const renderUserContent = () => {
        if (currentItems.length === 0) {
            return (
                <div className="user-admin-no-results">
                    <div className="user-admin-no-results-icon">
                        <i className="fas fa-search"></i>
                    </div>
                    <h4>Tidak ditemukan</h4>
                    <p>
                        {searchTerm
                            ? `Tidak ada user dengan kriteria "${searchTerm}"`
                            : `Tidak ada ${activeTab === 'users' ? 'user' : 'user dengan 2FA aktif'}`
                        }
                    </p>
                    {searchTerm && (
                        <button
                            className="user-admin-clear-search-btn"
                            onClick={() => setSearchTerm('')}
                        >
                            <i className="fas fa-times"></i>
                            Hapus pencarian
                        </button>
                    )}
                </div>
            );
        }

        if (viewMode === 'card') {
            return (
                <div className="user-admin-users-grid">
                    {currentItems.map((user) => (
                        <div key={user.username || user.id} className="user-admin-user-card">
                            <div className="user-admin-user-avatar">
                                <i className={`fas ${activeTab === 'users' ? 'fa-user' : 'fa-user-shield'}`}></i>
                            </div>
                            <div className="user-admin-user-info">
                                <h4>{user.username}</h4>
                                {activeTab === 'users' ? (
                                    <>
                                        <p className="user-admin-user-fullname">{user.fullname}</p>
                                        <p className="user-admin-user-role">{getRoleName(user.role)}</p>
                                        <p className="user-admin-user-email">{user.email}</p>
                                        {user.kl && <p className="user-admin-user-kl">{getKlName(user.kl)}</p>}
                                    </>
                                ) : (
                                    <p>2FA Aktif</p>
                                )}
                            </div>
                            <div className="user-admin-user-actions">
                                {activeTab === 'users' ? (
                                    <>
                                        <button
                                            className="user-admin-action-btn user-admin-reset-password-btn"
                                            onClick={() => openResetModal(user, 'password')}
                                            disabled={loading}
                                            title="Reset Password"
                                        >
                                            <i className="fas fa-key"></i>
                                        </button>
                                        {users2FA.some(u => u.username === user.username) && (
                                            <button
                                                className="user-admin-action-btn user-admin-reset-2fa-btn"
                                                onClick={() => openResetModal(user, '2fa')}
                                                disabled={loading}
                                                title="Reset 2FA"
                                            >
                                                <i className="fas fa-shield-alt"></i>
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <button
                                        className="user-admin-action-btn user-admin-reset-2fa-btn"
                                        onClick={() => openResetModal(user, '2fa')}
                                        disabled={loading}
                                        title="Reset 2FA"
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )
        }

        return (
            <div className="user-admin-table-container">
                <table className="user-admin-table">
                    <thead>
                        <tr>
                            {activeTab === 'users' ? (
                                <>
                                    <th>Username</th>
                                    <th>Nama Lengkap</th>
                                    <th>Role</th>
                                    <th>KL</th>
                                    <th>Email</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </>
                            ) : (
                                <>
                                    <th>Username</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((user) => (
                            <tr key={user.username || user.id}>
                                {activeTab === 'users' ? (
                                    <>
                                        <td data-label="Username"><strong>{user.username}</strong></td>
                                        <td data-label="Nama Lengkap">{user.fullname || '-'}</td>
                                        <td data-label="Role"><span className="user-admin-user-role">{getRoleName(user.role)}</span></td>
                                        <td data-label="KL">{getKlName(user.kl) || '-'}</td>
                                        <td data-label="Email">{user.email || '-'}</td>
                                        <td data-label="Actions">
                                            <div className="user-admin-table-actions">
                                                <button
                                                    className="user-admin-action-btn user-admin-reset-password-btn"
                                                    onClick={() => openResetModal(user, 'password')}
                                                    disabled={loading}
                                                    title="Reset Password"
                                                >
                                                    <i className="fas fa-key"></i>
                                                </button>
                                                {users2FA.some(u => u.username === user.username) && (
                                                    <button
                                                        className="user-admin-action-btn user-admin-reset-2fa-btn"
                                                        onClick={() => openResetModal(user, '2fa')}
                                                        disabled={loading}
                                                        title="Reset 2FA"
                                                    >
                                                        <i className="fas fa-shield-alt"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td data-label="Username"><strong>{user.username}</strong></td>
                                        <td data-label="Status"><span className="user-admin-status-active">2FA Aktif</span></td>
                                        <td data-label="Actions">
                                            <div className="user-admin-table-actions">
                                                <button
                                                    className="user-admin-action-btn user-admin-reset-2fa-btn"
                                                    onClick={() => openResetModal(user, '2fa')}
                                                    disabled={loading}
                                                    title="Reset 2FA"
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    const getPageTitle = () => {
        switch(activeTab) {
            case 'users': return 'Daftar User';
            case '2fa': return 'User dengan 2FA';
            case 'pending': return 'Pendaftaran Baru';
            default: return 'Manajemen User';
        }
    };

    const getPageSubtitle = () => {
        switch(activeTab) {
            case 'users': return 'Kelola akun pengguna sistem';
            case '2fa': return 'Kelola autentikasi dua faktor';
            case 'pending': return 'Persetujuan pendaftaran user baru';
            default: return 'Kelola pengguna sistem';
        }
    };

    return (
        <DashboardLayout menuItems={getAdminMenuItems()} userRole="Admin">
            <ToastContainer 
                position="top-center" 
                autoClose={5000} 
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
                style={{ zIndex: 99999 }}
            />
            <div className="management-tabs">
                <button 
                    className={`management-tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    <i className="fas fa-users"></i>
                    <span>Daftar User</span>
                    <span className="tab-counter">{users.length}</span>
                </button>
                <button 
                    className={`management-tab ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    <i className="fas fa-user-clock"></i>
                    <span>Pendaftaran Baru</span>
                    <span className="tab-counter" style={pendingUsers.filter(u => u.registration_status === 'pending_approval').length > 0 ? {background: '#dc3545'} : {}}>
                        {pendingUsers.filter(u => u.registration_status === 'pending_approval').length}
                    </span>
                </button>
                <button 
                    className={`management-tab ${activeTab === '2fa' ? 'active' : ''}`}
                    onClick={() => setActiveTab('2fa')}
                >
                    <i className="fas fa-shield-alt"></i>
                    <span>Pengaturan 2FA</span>
                    <span className="tab-counter">{users2FA.length}</span>
                </button>
            </div>

            <div className="management-page-header-modern">
                <div className="management-header-icon">
                    <i className={`fas ${activeTab === '2fa' ? 'fa-shield-alt' : 'fa-users'}`}></i>
                </div>
                <div className="management-header-content">
                    <h1 className="management-page-title-modern">
                        {getPageTitle()}
                    </h1>
                    <p className="management-page-subtitle-modern">
                        {getPageSubtitle()}
                    </p>
                </div>
                <div className="management-page-actions">
                    {activeTab === 'users' && (
                        <button
                            className="management-btn management-btn-primary"
                            onClick={() => setShowAddModal(true)}
                        >
                            <i className="fas fa-plus"></i>
                            <span>Tambah User</span>
                        </button>
                    )}
                    {activeTab === 'pending' && (
                        <button
                            className="management-btn management-btn-secondary"
                            onClick={() => fetchPendingUsers()}
                            disabled={loading}
                        >
                            <i className="fas fa-sync-alt"></i>
                            <span>Refresh</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Pending Users Tab Content */}
            {activeTab === 'pending' && (
                <div className="management-card">
                    {pendingUsers.length === 0 ? (
                        <div className="user-admin-empty-state">
                            <i className="fas fa-user-check" style={{fontSize: '3rem', color: '#28a745', marginBottom: '15px'}}></i>
                            <h4>Tidak Ada Pendaftaran Baru</h4>
                            <p>Semua pendaftaran sudah diproses</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="user-admin-table">
                                <thead>
                                    <tr>
                                        <th>Info User</th>
                                        <th>Instansi</th>
                                        <th>Kontak</th>
                                        <th>Status</th>
                                        <th>Tanggal Daftar</th>
                                        <th>Aksi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingUsers.map(user => (
                                        <tr key={user.id}>
                                            <td>
                                                <div className="user-admin-user-cell">
                                                    <div className="user-admin-avatar" style={{background: user.registration_status === 'pending_approval' ? '#17a2b8' : '#ffc107'}}>
                                                        {user.fullname?.charAt(0)?.toUpperCase() || user.username?.charAt(0)?.toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <strong>{user.fullname || '-'}</strong>
                                                        <div style={{fontSize: '0.85rem', color: '#6c757d'}}>@{user.username}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <span className={`badge ${user.user_type === 'kl' ? 'bg-primary' : 'bg-info'}`}>
                                                        {user.user_type === 'kl' ? 'K/L' : 'Pemda'}
                                                    </span>
                                                    <div style={{marginTop: '5px', fontSize: '0.9rem'}}>
                                                        {user.nama_kl || user.nama_pd || '-'}
                                                    </div>
                                                    {user.direktorat && (
                                                        <div style={{fontSize: '0.8rem', color: '#6c757d'}}>{user.direktorat}</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{fontSize: '0.9rem'}}>
                                                    <div><i className="fas fa-envelope" style={{marginRight: '5px', color: '#6c757d'}}></i>{user.email}</div>
                                                    {user.hp && <div><i className="fas fa-phone" style={{marginRight: '5px', color: '#6c757d'}}></i>{user.hp}</div>}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge ${
                                                    user.registration_status === 'pending_approval' ? 'bg-info' : 
                                                    user.registration_status === 'pending_otp' ? 'bg-warning text-dark' : 'bg-secondary'
                                                }`}>
                                                    {user.registration_status === 'pending_approval' ? 'Menunggu Persetujuan' : 
                                                     user.registration_status === 'pending_otp' ? 'Verifikasi Email' : user.registration_status}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{fontSize: '0.9rem'}}>
                                                    {user.registered_at ? new Date(user.registered_at).toLocaleDateString('id-ID', {
                                                        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                                    }) : '-'}
                                                </div>
                                            </td>
                                            <td>
                                                {user.registration_status === 'pending_approval' && (
                                                    <div style={{display: 'flex', gap: '8px'}}>
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => handleApproveUser(user.id)}
                                                            disabled={loading}
                                                            title="Setujui"
                                                        >
                                                            <i className="fas fa-check"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => {
                                                                setSelectedPendingUser(user);
                                                                setShowRejectModal(true);
                                                            }}
                                                            disabled={loading}
                                                            title="Tolak"
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </div>
                                                )}
                                                {user.registration_status === 'pending_otp' && (
                                                    <span style={{color: '#6c757d', fontSize: '0.85rem'}}>Menunggu verifikasi email</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Content Card for Users and 2FA */}
            {activeTab !== 'pending' && (
            <div className="management-card">
                {/* Search and View Options */}
                <div className="user-admin-search-container">
                    <div className="user-admin-search-input-group">
                        <i className="fas fa-search user-admin-search-icon"></i>
                        <input
                            type="text"
                            placeholder={`Cari ${activeTab === 'users' ? 'username, nama, atau email' : 'username'}...`}
                            className="user-admin-search-input"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        {searchTerm && (
                            <button
                                className="user-admin-search-clear"
                                onClick={() => setSearchTerm('')}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                    </div>

                    {activeTab === 'users' && (
                        <div className="user-admin-filter-group" style={{ display: 'flex', gap: '12px', marginLeft: '12px' }}>
                            <select 
                                className="form-select" 
                                value={filterRole} 
                                onChange={(e) => { setFilterRole(e.target.value); setCurrentPage(1); }}
                                style={{ width: '180px' }}
                            >
                                <option value="">Semua Role</option>
                                {roleOptions.map(role => (
                                    <option key={role.value} value={role.value}>{role.label}</option>
                                ))}
                            </select>
                            <select 
                                className="form-select" 
                                value={filterKL} 
                                onChange={(e) => { setFilterKL(e.target.value); setCurrentPage(1); }}
                                style={{ width: '250px' }}
                            >
                                <option value="">Semua K/L</option>
                                {klOptions.map(kl => (
                                    <option key={kl.kode_kl} value={kl.kode_kl}>{kl.nama_kl}</option>
                                ))}
                            </select>
                            {(filterRole || filterKL) && (
                                <button 
                                    className="btn btn-sm btn-outline-secondary" 
                                    onClick={() => { setFilterRole(''); setFilterKL(''); setCurrentPage(1); }}
                                    title="Reset Filter"
                                >
                                    <i className="fas fa-times"></i> Reset
                                </button>
                            )}
                        </div>
                    )}

                    <div className="user-admin-view-options">
                        <button
                            className={`user-admin-view-btn ${viewMode === 'table' ? 'active' : ''}`}
                            onClick={() => setViewMode('table')}
                            title="Tampilan Tabel"
                        >
                            <i className="fas fa-list"></i>
                        </button>
                        <button
                            className={`user-admin-view-btn ${viewMode === 'card' ? 'active' : ''}`}
                            onClick={() => setViewMode('card')}
                            title="Tampilan Kartu"
                        >
                            <i className="fas fa-th-large"></i>
                        </button>
                    </div>

                    <div className="user-admin-results-info">
                        {searchTerm ? (
                            <span>Menampilkan {currentData.length} dari {activeTab === 'users' ? users.length : users2FA.length} user</span>
                        ) : (
                            <span>Total {currentData.length} user</span>
                        )}
                    </div>
                </div>

                {/* Content: Table or Card Grid */}
                {renderUserContent()}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="user-admin-pagination-wrapper">
                                        <div className="user-admin-pagination">
                                            <button
                                                className="user-admin-pagination-btn user-admin-pagination-prev"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                <i className="fas fa-chevron-left"></i>
                                                Previous
                                            </button>

                                            <div className="user-admin-pagination-numbers">
                                                {[...Array(totalPages)].map((_, index) => {
                                                    const page = index + 1;
                                                    return (
                                                        <button
                                                            key={page}
                                                            className={`user-admin-pagination-number ${currentPage === page ? 'active' : ''}`}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <button
                                                className="user-admin-pagination-btn user-admin-pagination-next"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Next
                                                <i className="fas fa-chevron-right"></i>
                                            </button>
                                        </div>

                        <div className="user-admin-page-info">
                            Halaman {currentPage} dari {totalPages} •
                            Menampilkan {startIndex + 1}-{Math.min(endIndex, currentData.length)} dari {currentData.length} user
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && selectedPendingUser && (
                <div className="user-admin-modal-overlay" onClick={() => !loading && setShowRejectModal(false)}>
                    <div className="user-admin-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
                        <div className="user-admin-modal-header" style={{background: 'linear-gradient(135deg, #dc3545, #c82333)'}}>
                            <h3>
                                <i className="fas fa-user-times"></i>
                                Tolak Pendaftaran
                            </h3>
                            <button
                                className="user-admin-modal-close"
                                onClick={() => setShowRejectModal(false)}
                                disabled={loading}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="user-admin-modal-body" style={{padding: '20px'}}>
                            <p style={{marginBottom: '15px'}}>
                                Tolak pendaftaran dari <strong>{selectedPendingUser.fullname}</strong> ({selectedPendingUser.username})?
                            </p>
                            <div className="user-admin-input-group">
                                <label>Alasan Penolakan <span className="user-admin-required">*</span></label>
                                <textarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Masukkan alasan penolakan..."
                                    rows="3"
                                    style={{width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd'}}
                                    required
                                />
                            </div>
                        </div>
                        <div className="user-admin-modal-footer" style={{padding: '15px 20px', display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowRejectModal(false)}
                                disabled={loading}
                            >
                                Batal
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleRejectUser}
                                disabled={loading || !rejectReason.trim()}
                            >
                                {loading ? 'Memproses...' : 'Tolak Pendaftaran'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add User Modal */}
        {showAddModal && (
            <div className="user-admin-modal-overlay" onClick={() => !loading && setShowAddModal(false)}>
                <div className="user-admin-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="user-admin-modal-header">
                        <h3>
                            <i className="fas fa-user-plus"></i>
                            Tambah User Baru
                        </h3>
                        <button
                            className="user-admin-modal-close"
                            onClick={() => setShowAddModal(false)}
                            disabled={loading}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <form onSubmit={handleAddUser} className="user-admin-modal-body">
                        <div className="user-admin-form-container">
                            {/* Basic Information Section */}
                            <div className="user-admin-form-section">
                                <div className="user-admin-form-section-title">
                                    <i className="fas fa-user"></i>
                                    Informasi Dasar
                                </div>
                                    <div className="user-admin-form-row">
                                        <div className="user-admin-input-group">
                                            <label>
                                                Username <span className="user-admin-required">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="username"
                                                value={newUser.username}
                                                onChange={handleNewUserChange}
                                                disabled={loading}
                                                required
                                                placeholder="Masukkan username"
                                            />
                                            <div className="user-admin-input-hint">Username harus unik</div>
                                        </div>
                                        <div className="user-admin-input-group">
                                            <label>
                                                Password <span className="user-admin-required">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                name="password"
                                                value={newUser.password}
                                                onChange={handleNewUserChange}
                                                disabled={loading}
                                                required
                                                placeholder="Masukkan password"
                                                autoComplete="new-password"
                                            />
                                            <div className="user-admin-input-hint">Minimal 6 karakter</div>
                                        </div>
                                    </div>

                                    <div className="user-admin-form-row">
                                        <div className="user-admin-input-group">
                                            <label>
                                                Nama Lengkap
                                            </label>
                                            <input
                                                type="text"
                                                name="fullname"
                                                value={newUser.fullname}
                                                onChange={handleNewUserChange}
                                                disabled={loading}
                                                placeholder="Masukkan nama lengkap"
                                            />
                                            <div className="user-admin-input-hint">Opsional</div>
                                        </div>
                                        <div className="user-admin-input-group">
                                            <label>
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={newUser.email}
                                                onChange={handleNewUserChange}
                                                disabled={loading}
                                                placeholder="contoh@email.com"
                                            />
                                            <div className="user-admin-input-hint">Opsional</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact & Role Section */}
                                <div className="user-admin-form-section">
                                    <div className="user-admin-form-section-title">
                                        <i className="fas fa-id-card"></i>
                                        Kontak & Role
                                    </div>
                                    <div className="user-admin-form-row">
                                        <div className="user-admin-input-group">
                                            <label>No. HP</label>
                                            <input
                                                type="text"
                                                name="hp"
                                                value={newUser.hp}
                                                onChange={handleNewUserChange}
                                                disabled={loading}
                                                placeholder="08xxxxxxxxxx"
                                            />
                                            <div className="user-admin-input-hint">Opsional</div>
                                        </div>
                                        <div className="user-admin-input-group">
                                            <label>
                                                Role <span className="user-admin-required">*</span>
                                            </label>
                                            <select
                                                name="role"
                                                value={newUser.role}
                                                onChange={handleNewUserChange}
                                                disabled={loading}
                                                required
                                            >
                                                <option value="">Pilih Role</option>
                                                {roleOptions.map(role => (
                                                    <option key={role.value} value={role.value}>
                                                        {role.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="user-admin-form-section">
                                    <div className="user-admin-form-section-title">
                                        <i className="fas fa-building"></i>
                                        Informasi Organisasi
                                    </div>
                                    <div className="user-admin-input-group" style={{ marginBottom: '1.5rem' }}>
                                        <label>
                                            Kementerian/Lembaga <span className="user-admin-required">*</span>
                                        </label>
                                        <select
                                            name="kl"
                                            value={newUser.kl}
                                            onChange={handleNewUserChange}
                                            disabled={loading}
                                            required
                                        >
                                            <option value="">Pilih KL</option>
                                            {klOptions.map(kl => (
                                                <option key={kl.kode_kl} value={kl.kode_kl}>
                                                    {kl.nama_kl}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="user-admin-input-group">
                                        <label>Direktorat</label>
                                        <input
                                            type="text"
                                            name="direktorat"
                                            value={newUser.direktorat}
                                            onChange={handleNewUserChange}
                                            disabled={loading}
                                            placeholder="Nama direktorat"
                                        />
                                        <div className="user-admin-input-hint">Opsional</div>
                                    </div>
                                </div>
                            </div>

                            <div className="user-admin-modal-footer">
                                <button
                                    type="button"
                                    className="user-admin-btn user-admin-btn-secondary"
                                    onClick={() => setShowAddModal(false)}
                                    disabled={loading}
                                >
                                    <i className="fas fa-times"></i>
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    className="user-admin-btn user-admin-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <div className="user-admin-spinner"></div>
                                            Menyimpan...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            Simpan User
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset Confirmation Modal */}
            {showResetModal && selectedUser && (
                <div className="user-admin-modal-overlay" onClick={() => !loading && setShowResetModal(false)}>
                    <div className="user-admin-modal user-admin-modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="user-admin-modal-header">
                            <h3>
                                <i className={`fas ${resetType === 'password' ? 'fa-key' : 'fa-shield-alt'}`}></i>
                                Konfirmasi {resetType === 'password' ? 'Reset Password' : 'Reset 2FA'}
                            </h3>
                            <button
                                className="user-admin-modal-close"
                                onClick={() => setShowResetModal(false)}
                                disabled={loading}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="user-admin-modal-body">
                            <div className="user-admin-confirm-content">
                                <div className="user-admin-confirm-icon">
                                    <i className="fas fa-exclamation-triangle"></i>
                                </div>
                                <p>
                                    Apakah Anda yakin ingin {resetType === 'password' ? 'mereset password' : 'mereset 2FA'} untuk user <strong>"{selectedUser.username}"</strong>?
                                </p>
                                {resetType === 'password' && (
                                    <p className="user-admin-confirm-note">
                                        Password akan direset ke default: <strong>123456</strong>
                                    </p>
                                )}
                            </div>
                            <div className="user-admin-modal-footer">
                                <button
                                    type="button"
                                    className="user-admin-btn user-admin-btn-secondary"
                                    onClick={() => setShowResetModal(false)}
                                    disabled={loading}
                                >
                                    Batal
                                </button>
                                <button
                                    type="button"
                                    className={`user-admin-btn ${resetType === 'password' ? 'user-admin-btn-warning' : 'user-admin-btn-danger'}`}
                                    onClick={resetType === 'password' ? handleResetPassword : handleReset2FA}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <div className="user-admin-spinner"></div>
                                            Memproses...
                                        </>
                                    ) : (
                                        <>
                                            <i className={`fas ${resetType === 'password' ? 'fa-key' : 'fa-trash-alt'}`}></i>
                                            Ya, {resetType === 'password' ? 'Reset Password' : 'Reset 2FA'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default ManajemenUserPage;